/*
 * Result cards: real <video> preview at playhead_sec, scrub bar, verification
 * list, curator-note box. Ported from ui-handoff/.../results.jsx.
 *
 * The schemas.py Result uses snake_case (clip_id, segment_start_sec, …); we
 * keep that on the wire and adapt at render time. No data reshaping in
 * components — they read the API fields directly.
 */

import React from 'react';

function timeFromSeconds(s) {
  const seconds = s || 0;
  const m = Math.floor(seconds / 60);
  const ss = Math.floor(seconds % 60).toString().padStart(2, '0');
  const ms = Math.round((seconds - Math.floor(seconds)) * 1000).toString().padStart(3, '0');
  return `${m.toString().padStart(2, '0')}:${ss}.${ms}`;
}

function clamp01(value) {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

// NOTE (MLT-512 UI feedback): ScrubBar — the custom timeline with the segment
// highlight and the "matched" PLAYHEAD marker — is no longer rendered. Result
// cards now show the video only (no playhead); the playhead marker block below
// is retained but dead, and ResultCard no longer mounts <ScrubBar/>. Native
// <video> controls provide scrubbing.
function ScrubBar({ result }) {
  const duration = result.duration_sec || 1;
  const segStart = result.segment_start_sec || 0;
  const segEnd = result.segment_end_sec || 0;
  const playhead = result.playhead_sec || 0;
  const eventTicks = result.event_ticks || [];

  const segL = clamp01(segStart / duration) * 100;
  const segW = Math.max(0, clamp01((segEnd - segStart) / duration) * 100);
  const phP = clamp01(playhead / duration) * 100;
  // A short segment on a long clip can render as a 2%-wide sliver that
  // disappears at-a-glance. Enforce a minimum visual width so the curator
  // always sees the highlight; numeric duration is shown in the label below.
  // Clamp against (100 - segL) so a near-right-edge match doesn't overflow
  // the rail and push the right end-cap past the axis.
  const segDisplayW = Math.min(Math.max(segW, 2.5), Math.max(0, 100 - segL));
  const windowCount = eventTicks.length;

  return (
    <div className="scrub">
      <div className="scrub-rail">
        <div className="scrub-track" />
        {eventTicks.map((p, i) => (
          <div key={i} className="scrub-tick scrub-tick-window" style={{ left: `${p * 100}%` }} />
        ))}
        <div className="scrub-segment" style={{ left: `${segL}%`, width: `${segDisplayW}%` }}>
          <span className="scrub-seg-end-l" />
          <span className="scrub-seg-end-r" />
        </div>
        <div className="scrub-playhead" style={{ left: `${phP}%` }}>
          <div className="scrub-playhead-bar" />
          <div className="scrub-playhead-chip mono">
            <span className="scrub-playhead-arrow">▸</span>
            <span className="num">{result.timestamp}</span>
            <span className="scrub-playhead-label">matched</span>
          </div>
        </div>
      </div>
      <div className="scrub-axis mono num">
        <span>00:00</span>
        <span className="scrub-seg-label">
          <span className="scrub-seg-label-dot" />
          matched {timeFromSeconds(segStart).slice(0, 8)} – {timeFromSeconds(segEnd).slice(0, 8)}
          <span className="scrub-seg-label-dur">· {(segEnd - segStart).toFixed(1)}s</span>
          {windowCount > 1 && (
            <span className="scrub-seg-label-count">· {windowCount} windows</span>
          )}
        </span>
        <span>{timeFromSeconds(duration).slice(0, 5)}</span>
      </div>

      <style>{`
        .scrub { display: flex; flex-direction: column; gap: 6px; }
        .scrub-rail { position: relative; height: 38px; }
        .scrub-track { position: absolute; left: 0; right: 0; top: 18px; height: 2px; background: var(--rule-2); }
        .scrub-tick { position: absolute; top: 13px; width: 1px; height: 12px; background: var(--ink-4); opacity: .55; }
        /* Per-window markers (event_ticks). One tick per matched 2s narrative
           window — stands taller than the generic ticks and uses the accent
           so the curator can see each matched moment inside the segment. */
        .scrub-tick-window {
          top: 7px; height: 24px; width: 2px;
          background: var(--accent); opacity: 1;
          box-shadow: 0 0 0 2px var(--bg);
          z-index: 1;
        }
        .scrub-segment {
          position: absolute; top: 8px; height: 22px;
          background: var(--accent);
          opacity: 0.28;
          border-top: 2px solid var(--accent);
          border-bottom: 2px solid var(--accent);
          border-radius: 1px;
        }
        .scrub-seg-end-l, .scrub-seg-end-r {
          position: absolute; top: -4px; bottom: -4px; width: 1.5px; background: var(--accent);
        }
        .scrub-seg-end-l { left: 0; }
        .scrub-seg-end-r { right: 0; }
        .scrub-playhead { position: absolute; top: 0; bottom: 0; pointer-events: none; }
        .scrub-playhead-bar {
          position: absolute; left: -1px; top: 4px; bottom: 4px; width: 2px;
          background: var(--accent);
          box-shadow: 0 0 0 3px var(--bg), 0 0 12px var(--accent);
        }
        .scrub-playhead-chip {
          position: absolute; left: 0; bottom: -30px; transform: translateX(-50%);
          background: var(--accent); color: #fff8ee;
          font-size: 11px; padding: 3px 8px 3px 6px;
          display: inline-flex; align-items: center; gap: 6px;
          white-space: nowrap;
          letter-spacing: 0.02em;
          font-weight: 500;
        }
        .scrub-playhead-arrow { font-size: 10px; opacity: 0.9; }
        .scrub-playhead-label { font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.8; }
        .scrub-axis { display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: var(--ink-3); padding-top: 44px; }
        .scrub-seg-label { display: inline-flex; align-items: center; gap: 6px; color: var(--accent); }
        .scrub-seg-label-dot { width: 7px; height: 7px; background: var(--accent-2); border: 1px solid var(--accent); display: inline-block; }
        .scrub-seg-label-dur { color: var(--ink-3); }
        .scrub-seg-label-count { color: var(--ink-3); font-style: italic; }
      `}</style>
    </div>
  );
}

function VideoFrame({ result }) {
  const [errored, setErrored] = React.useState(false);
  const clipId = result.clip_id;

  // MLT-512 UI feedback: show the clip video only — no playhead. Retrieval is
  // clip-level (the whole clip is the result), so we open at t=0 and let the
  // native HTML5 controls drive play/pause/scrub. The previous seek-to-
  // playhead_sec logic (videoRef + onLoadedMetadata) is removed/commented:
  //
  //   const playhead = result.playhead_sec ?? 0;
  //   const handleLoadedMetadata = () => { videoRef.current.currentTime = playhead; };
  //
  return (
    <div className="vframe">
      <div className="vframe-scene">
        {errored ? (
          <div className="vframe-error mono">
            video unavailable —{' '}
            <code>python -m pipelines.curation.build_clip_videos</code>
          </div>
        ) : (
          <video
            className="vframe-video"
            src={`/clips/${clipId}.mp4`}
            preload="metadata"
            playsInline
            controls
            onError={() => setErrored(true)}
          />
        )}
        <div className="vframe-overlay">
          <div className="vframe-tl mono">
            <span>{result.clip_id}</span>
            <span className="vframe-dot">·</span>
            <span>{result.cam}</span>
          </div>
          <div className="vframe-tr mono">
            <span>
              {result.date} {result.captured}
            </span>
          </div>
          <div className="vframe-br mono">
            <span className="num">{result.timestamp}</span>
            <span className="vframe-dim">
              {' '}/ {timeFromSeconds(result.duration_sec).slice(0, 5)}
            </span>
          </div>
          {result.location && <div className="vframe-bl mono">{result.location}</div>}
        </div>
      </div>
      <style>{`
        .vframe { position: relative; aspect-ratio: 16/9; background: #000; overflow: hidden; }
        .vframe-scene { position: absolute; inset: 0; }
        .vframe-video { width: 100%; height: 100%; object-fit: cover; display: block; background: #000; }
        .vframe-error {
          position: absolute; inset: 0; display: grid; place-items: center;
          color: rgba(255,255,255,0.7); font-size: 11px; text-align: center; padding: 12px;
        }
        .vframe-error code { color: rgba(255,255,255,0.9); }
        .vframe-overlay { position: absolute; inset: 0; pointer-events: none; }
        .vframe-tl, .vframe-tr, .vframe-bl, .vframe-br {
          position: absolute; font-size: 10.5px; letter-spacing: 0.04em; color: rgba(255,255,255,0.92);
          padding: 5px 8px; background: rgba(0,0,0,0.4); backdrop-filter: blur(3px);
          display: inline-flex; align-items: center; gap: 6px;
        }
        .vframe-tl { top: 10px; left: 10px; max-width: 60%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .vframe-tr { top: 10px; right: 10px; }
        /* Native HTML5 controls bar sits at bottom — lift bl/br above it so
           the timestamp/location chips stay visible while the user scrubs. */
        .vframe-bl { bottom: 48px; left: 10px; }
        .vframe-br { bottom: 48px; right: 10px; }
        .vframe-dim { color: rgba(255,255,255,0.55); }
        .vframe-dot { opacity: 0.5; }
      `}</style>
    </div>
  );
}

function VerificationList({ items }) {
  return (
    <ul className="verify-list">
      {(items || []).map((c, i) => (
        <li key={`${c.label}-${i}`} className="verify-row">
          <span
            className={`verify-icon verify-${c.ok === 'warn' ? 'warn' : c.ok ? 'ok' : 'bad'}`}
            aria-hidden
          >
            {c.ok === 'warn' ? '!' : c.ok ? '✓' : '✗'}
          </span>
          <span className="verify-label mono">{c.label}</span>
          <span className="verify-detail">{c.detail}</span>
        </li>
      ))}
      <style>{`
        .verify-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
        .verify-row { display: grid; grid-template-columns: 16px 110px 1fr; gap: 10px; align-items: baseline; font-size: 13px; }
        .verify-icon { display: inline-grid; place-items: center; width: 16px; height: 16px; font-size: 11px; font-weight: 600; }
        .verify-ok { color: var(--pos); }
        .verify-warn { color: var(--warn); }
        .verify-bad { color: var(--accent); }
        .verify-label { font-size: 11px; letter-spacing: 0.04em; color: var(--ink-3); text-transform: lowercase; }
        .verify-detail { color: var(--ink-2); }
      `}</style>
    </ul>
  );
}

function CommentBox({ comment, onSave, status }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(comment || '');
  const taRef = React.useRef(null);

  React.useEffect(() => {
    setDraft(comment || '');
  }, [comment]);

  React.useEffect(() => {
    if (editing && taRef.current) taRef.current.focus();
  }, [editing]);

  const isSaving = status === 'saving';

  if (!editing && !comment) {
    return (
      <button type="button" className="cmt-trigger mono" onClick={() => setEditing(true)}>
        <svg viewBox="0 0 24 24" width="12" height="12" style={{ marginRight: 6 }}>
          <path d="M4 4 H20 V16 H8 L4 20 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        add curator note
        <CommentStyles />
      </button>
    );
  }

  if (!editing && comment) {
    return (
      <div
        className="cmt-saved"
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setEditing(true);
        }}
      >
        <div className="cmt-saved-hd mono">
          <span className="cmt-saved-icon">✎</span>
          <span>curator note</span>
        </div>
        <div className="cmt-saved-body">{comment}</div>
        <CommentStyles />
      </div>
    );
  }

  return (
    <div className="cmt-edit">
      <div className="cmt-edit-hd mono">
        curator note
        {status === 'error' && (
          <span style={{ color: 'var(--accent)', marginLeft: 8, textTransform: 'none' }}>
            · save failed
          </span>
        )}
      </div>
      <textarea
        ref={taRef}
        className="cmt-ta"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="What did the agent get right or miss? — playhead timing, conflict actor, confidence..."
        rows={3}
      />
      <div className="cmt-actions">
        <button
          type="button"
          className="cmt-btn cmt-btn-ghost mono"
          onClick={() => {
            setDraft(comment || '');
            setEditing(false);
          }}
        >
          cancel
        </button>
        <button
          type="button"
          className="cmt-btn cmt-btn-primary mono"
          disabled={isSaving}
          onClick={() => {
            onSave(draft);
            setEditing(false);
          }}
        >
          {isSaving ? 'saving…' : 'save note'}
        </button>
      </div>
      <CommentStyles />
    </div>
  );
}

function CommentStyles() {
  return (
    <style>{`
      .cmt-trigger {
        align-self: flex-start;
        background: transparent; border: 1px dashed var(--rule-2); color: var(--ink-3);
        padding: 6px 10px; font-size: 11px; letter-spacing: 0.04em;
        display: inline-flex; align-items: center;
      }
      .cmt-trigger:hover { color: var(--accent); border-color: var(--accent); }
      .cmt-saved { border-left: 2px solid var(--accent); background: var(--panel-2); padding: 8px 12px; cursor: text; }
      .cmt-saved-hd { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
      .cmt-saved-icon { color: var(--accent); }
      .cmt-saved-body { font-size: 13px; color: var(--ink); font-family: var(--serif); font-style: italic; line-height: 1.45; }
      .cmt-edit { border: 1px solid var(--rule-2); background: var(--panel); padding: 10px 12px; }
      .cmt-edit-hd { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 6px; }
      .cmt-ta { width: 100%; resize: vertical; border: 0; background: transparent; color: var(--ink); font-family: var(--serif); font-size: 14px; line-height: 1.5; padding: 0; }
      .cmt-ta::placeholder { color: var(--ink-4); font-style: italic; }
      .cmt-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
      .cmt-btn { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; padding: 5px 11px; border: 1px solid var(--rule-2); background: var(--panel); color: var(--ink-2); }
      .cmt-btn-primary { background: var(--ink); color: var(--bg); border-color: var(--ink); }
      .cmt-btn-primary:hover:not(:disabled) { background: var(--accent); border-color: var(--accent); }
      .cmt-btn-primary:disabled { opacity: 0.5; }
      .cmt-btn-ghost:hover { color: var(--ink); }
    `}</style>
  );
}

export function ResultCard({ result, index, comment, commentStatus, onComment }) {
  return (
    <article className="rcard fade-in" style={{ animationDelay: `${index * 120}ms` }}>
      <div className="rcard-l">
        <VideoFrame result={result} />
        {/* MLT-512 UI feedback: playhead/scrub bar removed — show the video only. */}
        {/* <ScrubBar result={result} /> */}
      </div>
      <div className="rcard-r">
        <header className="rcard-hd">
          <div className="rcard-rank mono">match {String(index + 1).padStart(2, '0')}</div>
          <div className="rcard-conf">
            <div className="rcard-conf-bar">
              <div style={{ width: `${(result.confidence || 0) * 100}%` }} />
            </div>
            <span className="mono num">{(result.confidence || 0).toFixed(2)}</span>
          </div>
        </header>
        <h3 className="rcard-headline serif">{result.headline}</h3>

        <section className="rcard-block">
          <div className="rcard-block-hd mono">verification</div>
          <VerificationList items={result.verification} />
        </section>

        <section className="rcard-block">
          <CommentBox comment={comment} status={commentStatus} onSave={onComment} />
        </section>
      </div>

      <style>{`
        .rcard {
          display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          gap: 32px; padding: 28px 0; border-bottom: 1px solid var(--rule);
        }
        .rcard:last-child { border-bottom: 0; }
        .rcard-l { display: flex; flex-direction: column; gap: 18px; }
        .rcard-r { display: flex; flex-direction: column; gap: 18px; }
        .rcard-hd { display: flex; align-items: center; justify-content: space-between; }
        .rcard-rank { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-3); }
        .rcard-conf { display: flex; align-items: center; gap: 10px; font-size: 11px; color: var(--ink-3); }
        .rcard-conf-bar { width: 90px; height: 3px; background: var(--rule-2); position: relative; }
        .rcard-conf-bar > div { position: absolute; left: 0; top: 0; bottom: 0; background: var(--accent); }
        .rcard-headline {
          font-size: 26px; line-height: 1.18; letter-spacing: -0.005em;
          font-weight: 400; color: var(--ink); margin: 0;
          text-wrap: pretty;
        }
        .rcard-block { display: flex; flex-direction: column; gap: 10px; }
        .rcard-block-hd { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); }
      `}</style>
    </article>
  );
}

export function ResultsHeader({ query, count }) {
  return (
    <div className="rh">
      <div className="rh-l">
        <div className="mono micro" style={{ color: 'var(--ink-3)' }}>RESULTS</div>
        <h2 className="rh-h serif">
          <span className="num">{count}</span> verified {count === 1 ? 'clip' : 'clips'}
          <span className="rh-h-dim"> matched </span>
          <em>"{query}"</em>
        </h2>
      </div>
      <div className="rh-r mono micro">sorted by confidence · ranked</div>
      <style>{`
        .rh { display: flex; justify-content: space-between; align-items: baseline; gap: 24px; padding: 28px 0 18px; border-bottom: 1px solid var(--rule); }
        .rh-h { font-size: 26px; line-height: 1.2; font-weight: 400; margin: 4px 0 0; color: var(--ink); letter-spacing: -0.005em; }
        .rh-h-dim { color: var(--ink-3); }
        .rh-h em { font-style: italic; color: var(--accent); }
      `}</style>
    </div>
  );
}

export function LoadingSkeleton() {
  return (
    <div className="loading-state fade-in">
      <div className="loading-hd">
        <div className="mono micro" style={{ color: 'var(--accent)' }}>AGENT WORKING</div>
        <h2 className="loading-h serif">
          Streaming verification across candidate clips…
        </h2>
        <p className="loading-sub">
          Reasoning is shown on the right — every tool call, every check. Results will appear here
          in confidence order as soon as verification clears.
        </p>
      </div>
      <div className="loading-skeleton">
        {[0, 1, 2].map((i) => (
          <div key={i} className="lskel" style={{ animationDelay: `${i * 0.15}s` }}>
            <div className="lskel-vid" />
            <div className="lskel-meta">
              <div className="lskel-bar lskel-bar-1" />
              <div className="lskel-bar lskel-bar-2" />
              <div className="lskel-bar lskel-bar-3" />
            </div>
          </div>
        ))}
      </div>
      <style>{`
        .loading-state { padding: 36px 0; }
        .loading-h { font-size: 32px; line-height: 1.2; font-weight: 400; color: var(--ink); margin: 6px 0 12px; max-width: 580px; text-wrap: balance; letter-spacing: -0.005em; }
        .loading-sub { color: var(--ink-2); max-width: 540px; font-size: 14px; line-height: 1.55; margin: 0; }
        .loading-skeleton { display: flex; flex-direction: column; gap: 22px; margin-top: 44px; }
        .lskel { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); gap: 32px; padding: 24px 0; border-bottom: 1px solid var(--rule); opacity: 0; animation: skel-in .6s ease forwards; }
        @keyframes skel-in { to { opacity: 1; } }
        .lskel-vid { aspect-ratio: 16/9; background: linear-gradient(90deg, var(--panel-2) 0%, var(--panel) 50%, var(--panel-2) 100%); background-size: 200% 100%; animation: shimmer 1.6s ease-in-out infinite; }
        .lskel-meta { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
        .lskel-bar { height: 14px; background: linear-gradient(90deg, var(--panel-2) 0%, var(--panel) 50%, var(--panel-2) 100%); background-size: 200% 100%; animation: shimmer 1.6s ease-in-out infinite; }
        .lskel-bar-1 { width: 70%; height: 24px; }
        .lskel-bar-2 { width: 90%; }
        .lskel-bar-3 { width: 50%; }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
}
